import type { ReactNode } from "react";
import { Eye, FolderInput, Share2, Sparkles, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { Badge, Button } from "@/components/ui";
import { FileThumbnail } from "@/components/vault/FileThumbnail";
import { VAULT_SOURCES } from "@/mockData/vaultNav";
import type { VaultFileItem } from "@/types/vault.types";

interface FileDetailPanelProps {
  file: VaultFileItem;
  onClose: () => void;
  onView: () => void;
  onDownload: () => void;
  onDelete?: () => void;
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border py-2 last:border-b-0">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="text-right text-xs text-foreground">{value}</span>
    </div>
  );
}

function getSourceName(sourceId?: string) {
  if (!sourceId) return "Equal IQ Vault";
  return VAULT_SOURCES.find((s) => s.id === sourceId)?.name ?? sourceId;
}

export function FileDetailPanel({ file, onClose, onView, onDownload, onDelete }: FileDetailPanelProps) {
  const ext = file.extension ? file.extension.slice(1).toUpperCase() : file.fileIcon.toUpperCase();

  return (
    <aside className="flex w-80 shrink-0 flex-col overflow-hidden border-l border-border">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="text-[10.5px] font-semibold tracking-wide text-muted-foreground uppercase">
          File detail
        </span>
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close detail panel">
          <X className="size-4" />
        </Button>
      </div>

      <div className="flex flex-col items-center gap-3 border-b border-border px-4 py-5">
        <div className="flex h-[148px] w-[120px] items-center justify-center rounded-lg border border-border bg-muted/40">
          <FileThumbnail fileType={file.fileIcon} className="h-[120px] w-[96px] rounded-lg text-base" />
        </div>
        <div className="max-w-full text-center">
          <p className="text-sm font-semibold break-words text-foreground">{file.name}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {file.sizeFormatted} · {ext}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5 border-b border-border p-3">
        <Button size="sm" className="gap-1" onClick={onView}>
          <Eye className="size-3.5" aria-hidden="true" />
          Open
        </Button>
        <Button size="sm" variant="outline" className="gap-1" onClick={() => toast.info("Share coming soon")}>
          <Share2 className="size-3.5" aria-hidden="true" />
          Share
        </Button>
        <Button size="sm" variant="outline" className="gap-1" onClick={() => toast.info("Move coming soon")}>
          <FolderInput className="size-3.5" aria-hidden="true" />
          Move
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={() => (onDelete ? onDelete() : toast.info("Delete coming soon"))}
        >
          <Trash2 className="size-3.5" aria-hidden="true" />
          Delete
        </Button>
      </div>

      <div className="flex-1 overflow-auto px-4 py-3">
        <p className="mb-2 text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">Details</p>
        <InfoRow label="Source" value={getSourceName(file.sourceId)} />
        <InfoRow label="Modified" value={file.updatedAtFormatted || file.date} />
        <InfoRow label="Sync status" value={<span className="text-success">● Synced</span>} />
        <InfoRow label="Shared" value={file.shared ? "Yes" : "Private"} />
        <InfoRow label="Folder" value={file.folderPath ?? "—"} />

        {(file.tags?.length ?? 0) > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">Tags</p>
            <div className="flex flex-wrap gap-1">
              {file.tags!.map((tag) => (
                <Badge key={tag} variant="outline" className="text-[10px]">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-primary">
            <Sparkles className="size-3.5" aria-hidden="true" />
            Ari&apos;s summary
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {file.description ??
              `This ${ext} file appears related to ${file.tags?.[0] ?? "your vault"} workflows. Review terms and parties before sharing externally.`}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2 h-7 w-full text-xs"
            onClick={() => toast.info("Summarize is coming soon")}
          >
            Summarize
          </Button>
        </div>
      </div>

      <div className="border-t border-border p-3">
        <Button size="sm" variant="outline" className="w-full gap-1.5" onClick={onDownload}>
          Download
        </Button>
      </div>
    </aside>
  );
}
